import type { ReactElement, ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from '@govard/i18n-core/react';
import { EmptyState } from '../../../components/entity-detail/EmptyState.js';
import { EntityHistoryFeed } from '../../../components/entity-detail/EntityHistoryFeed.js';
import { EntityRelatedList } from '../../../components/entity-detail/EntityRelatedList.js';
import { EntityTaskDueChip } from '../../../components/entity-detail/EntityTaskDueChip.js';
import { formatDateTime } from '../../../shared/format/entity-detail.js';
import type { ContactItem } from '../../contacts/api/contacts.client.js';
import type { DealListItem } from '../../deals/api/deals.client.js';
import type { NoteItem } from '../../notes/api/notes.client.js';
import type { TaskItem } from '../../tasks/api/tasks.client.js';
import type { CompanyHistoryItem } from '../hooks/useCompanyHistory.js';
import { companyContactRow } from './CompanyContactsTab.js';
import { companyDealRow, isDealOpen } from './CompanyDealsTab.js';
import { companyHistoryFeedItem } from './CompanyHistoryTab.js';
import { isTaskOpen } from './CompanySidebar.js';

const LIMIT = 5;

export function CompanyOverview({
  contacts,
  contactsLoading,
  deals,
  dealsLoading,
  tasks,
  notes,
  history,
}: {
  contacts: ContactItem[];
  contactsLoading: boolean;
  deals: DealListItem[];
  dealsLoading: boolean;
  tasks: TaskItem[];
  notes: NoteItem[];
  history: CompanyHistoryItem[];
}): ReactElement {
  const { t, locale } = useTranslation();
  const [params] = useSearchParams();
  const tabHref = (tab: string): string => {
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    return `?${next.toString()}`;
  };
  const section = (
    testId: string,
    title: string,
    tab: string | null,
    children: ReactNode,
  ): ReactElement => (
    <section className="entity-panel" data-testid={testId}>
      <div className="entity-list-toolbar">
        <h3>{title}</h3>
        {tab ? (
          <Link data-testid={`${testId}-all`} to={tabHref(tab)}>
            {t('crm.features.company_detail.all')}
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
  const topContacts = [...contacts]
    .sort((a, b) => b.dealsCount - a.dealsCount || a.fullName.localeCompare(b.fullName, locale))
    .slice(0, LIMIT);
  const openDeals = deals
    .filter(isDealOpen)
    .sort(
      (a, b) =>
        (a.nextActivityDue ? Date.parse(a.nextActivityDue) : Infinity) -
          (b.nextActivityDue ? Date.parse(b.nextActivityDue) : Infinity) ||
        a.title.localeCompare(b.title, locale),
    );
  // Открытые задачи: сначала просроченные, затем ближайшие, без срока — в конце.
  const due = (task: TaskItem): number => (task.dueAt ? Date.parse(task.dueAt) : Infinity);
  const openTasks = tasks
    .filter(isTaskOpen)
    .sort((a, b) => due(a) - due(b) || a.title.localeCompare(b.title, locale));
  const recentNotes = [...notes]
    .sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt))
    .slice(0, 3);
  return (
    <div className="company-overview-sections">
      <div className="entity-overview">
        {section(
          'company-overview-contacts',
          t('crm.features.company_detail.contacts'),
          contacts.length ? 'contacts' : null,
          <EntityRelatedList
            loading={contactsLoading}
            emptyText={t(
              'crm.features.features.companies.pages.companydetailpage.к_компании_пока_не_привязан_ни_один_контак',
            )}
            rows={topContacts.map((contact) => companyContactRow(contact, t))}
          />,
        )}
        {section(
          'company-overview-deals',
          t('crm.features.company_detail.open_deals'),
          deals.length ? 'deals' : null,
          <EntityRelatedList
            loading={dealsLoading}
            emptyText={t('crm.features.company_detail.empty_open_deals')}
            rows={openDeals.slice(0, LIMIT).map((deal) => companyDealRow(deal, t))}
          />,
        )}
        {section(
          'company-overview-tasks',
          t('crm.features.company_detail.open_tasks', { count: openTasks.length }),
          tasks.length ? 'tasks' : null,
          openTasks.length ? (
            openTasks.slice(0, LIMIT).map((task) => (
              <div key={task.id} className="entity-overview-action company-overview-task">
                <Link to={`/tasks/${task.id}`}>{task.title}</Link>
                <EntityTaskDueChip dueAt={task.dueAt ?? null} />
              </div>
            ))
          ) : (
            <EmptyState text={t('crm.features.company_detail.empty_open_tasks')} />
          ),
        )}
        {section(
          'company-overview-notes',
          t('crm.features.company_detail.recent_notes'),
          notes.length ? 'notes' : null,
          recentNotes.length ? (
            recentNotes.map((note) => (
              <article key={note.id} className="company-overview-note">
                <div className="entity-meta">
                  {note.createdByMember?.user.name ||
                    t(
                      'crm.features.features.notes.components.entitynotespanel.неизвестный_автор',
                    )}{' '}
                  · <time dateTime={note.createdAt}>{formatDateTime(note.createdAt)}</time>
                </div>
                <p className="entity-note-text">{note.body}</p>
              </article>
            ))
          ) : (
            <EmptyState text={t('crm.entity_detail.empty_notes')} />
          ),
        )}
        {section(
          'company-overview-history',
          t('crm.entity_detail.recent_history'),
          history.length ? 'history' : null,
          <EntityHistoryFeed items={history.slice(0, 3).map(companyHistoryFeedItem)} />,
        )}
      </div>
    </div>
  );
}
